import React, { createContext, useCallback, useContext, useState } from 'react';

import { DailyLimitModal } from '../../components/_molecules/DailyLimitModal/DailyLimitModal';
import useAccessBadge, { LicenseCoarseLevels } from '../../hooks/useAccessBadge';
import useDailyAbuseLimit from '../../hooks/useDailyAbuseLimit';

interface DailyLimitContextType {
  isDailyLimitBlocked: boolean;
  promptDailyLimit: () => boolean;
}

const DailyLimitContext = createContext<DailyLimitContextType | undefined>(undefined);

export const DailyLimitProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { isBlocked } = useDailyAbuseLimit();
  const { accessBadge } = useAccessBadge();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isFreemium = accessBadge?.license?.coarseLevel === LicenseCoarseLevels.freemium;
  const isEffectivelyBlocked = isBlocked && isFreemium;

  const promptDailyLimit = useCallback(() => {
    if (isEffectivelyBlocked) {
      setIsModalOpen(true);
      return true;
    }
    return false;
  }, [isEffectivelyBlocked]);

  return (
    <DailyLimitContext.Provider value={{ isDailyLimitBlocked: isEffectivelyBlocked, promptDailyLimit }}>
      {children}
      <DailyLimitModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </DailyLimitContext.Provider>
  );
};

export const useDailyLimit = (): DailyLimitContextType => {
  const context = useContext(DailyLimitContext);
  if (context === undefined) {
    throw new Error('useDailyLimit must be used within a DailyLimitProvider');
  }
  return context;
};
